import React, { useEffect, useState } from 'react';
import { useDispatch, connect } from 'react-redux';
import { Search, Check, AlertCircle, RefreshCw, UserCheck } from 'lucide-react';

import { 
  setPolicyNumber, 
  fetchMissingBeneficiaries, 
  syncBeneficiaries,
  clearError,
  clearNotification
} from '../../../store/actions/ContratActions';
import { formatDate, formatValue } from '../../../Utils/formatters';

const BeneficiariesSyncApp = (props) => {
  const dispatch = useDispatch();
  
  // Local state as backup in case Redux is slow to update
  const [localPolicyNumber, setLocalPolicyNumber] = useState('');
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // On mount, check if the URL contains a policyNumber parameter.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const policyNumberFromUrl = params.get('policyNumber');
    if (policyNumberFromUrl) {
      setLocalPolicyNumber(policyNumberFromUrl);
      dispatch(setPolicyNumber(policyNumberFromUrl));
      // Uncomment the next two lines to automatically trigger a search if desired.
       setSearchPerformed(true);
       dispatch(fetchMissingBeneficiaries(policyNumberFromUrl));
    }
  }, [dispatch]);

  // Update local state when Redux props change
  useEffect(() => {
    setBeneficiaries(props.beneficiaries || []);
    setError(props.errorMessage);
    setSyncResult(props.syncResult);
    setLoading(props.showLoading);
  }, [props.beneficiaries, props.errorMessage, props.successMessage, props.showLoading, props.syncResult]);

  // For input control, update local state and dispatch Redux action
  const handlePolicyNumberChange = (e) => {
    const newValue = e.target.value;
    setLocalPolicyNumber(newValue);
    dispatch(setPolicyNumber(newValue));
  };

  const handleSearch = () => {
    setSearchPerformed(true);
    dispatch(fetchMissingBeneficiaries(localPolicyNumber));
  };

  const handleSync = () => {
    dispatch(syncBeneficiaries(localPolicyNumber));
  };

  // Clear notifications after 5 seconds
  useEffect(() => {
    let errorTimer, syncTimer;
    if (error) {
      errorTimer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
    }
    
    if (syncResult) {
      syncTimer = setTimeout(() => {
        dispatch(clearNotification());
      }, 5000);
    }
    
    return () => {
      clearTimeout(errorTimer);
      clearTimeout(syncTimer);
    };
  }, [error, syncResult, dispatch]);

  return (
    <div className="container-fluid bg-light py-4">
      <div className="container">
        <div className="card shadow mb-4">
          <div className="card-body">
            <h1 className="card-title text-primary mb-4">Synchronisation des Bénéficiaires</h1>
            
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text">
                    <Search size={20} />
                  </span>
                  <input
                    type="text"
                    value={localPolicyNumber}
                    onChange={handlePolicyNumberChange}
                    placeholder="Entrez le numéro de police"
                    className="form-control"
                    autoComplete="off"
                  />
                </div>
              </div>
              
              <div className="col-md-6 d-flex gap-2 mt-3 mt-md-0">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="btn btn-primary d-flex align-items-center gap-2"
                >
                  <Search size={18} />
                  Chercher
                </button>
                
                <button
                  onClick={handleSync}
                  disabled={loading}
                  className="btn btn-success d-flex align-items-center gap-2"
                >
                  {loading ? (
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <RefreshCw size={18} />
                  )}
                  Synchroniser
                </button>
              </div>
            </div>
            
            {error && (
              <div className="alert alert-danger d-flex align-items-start">
                <AlertCircle className="me-2 mt-1 flex-shrink-0" size={20} />
                <div>{error}</div>
              </div>
            )}
            
            {syncResult && (
              <div className="alert alert-success d-flex align-items-start">
                <Check className="me-2 mt-1 flex-shrink-0" size={20} />
                <div>
                  Synchronisation réussie ! {syncResult.BeneficiariesInserted} bénéficiaires insérés.
                </div>
              </div>
            )}
          </div>
        </div>
        
        {searchPerformed && (
          <div className="card shadow">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <UserCheck size={20} />
                <h5 className="mb-0 text-primary">Bénéficiaires non synchronisés</h5>
              </div>
              <span className="badge bg-primary rounded-pill">
                {beneficiaries.length} trouvés
              </span>
            </div>
            
            {beneficiaries.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th scope="col" className="text-uppercase small">
                        Nom & Prénom
                      </th>
                      <th scope="col" className="text-uppercase small">
                        N° Bénéficiaire
                      </th>
                      <th scope="col" className="text-uppercase small">
                        N° Bénéf. Principal
                      </th>
                      <th scope="col" className="text-uppercase small">
                        Lien
                      </th>
                      <th scope="col" className="text-uppercase small">
                        Date de naissance
                      </th>
                      <th scope="col" className="text-uppercase small">
                        Téléphone
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {beneficiaries.map((beneficiary, index) => (
                      <tr key={index}>
                        <td>
                          <div>
                            <div className="fw-medium">
                              {formatValue(beneficiary.nom)} {formatValue(beneficiary.prenoms)}
                            </div>
                            <div className="text-muted small">
                              {formatValue(beneficiary.sexe)}
                            </div>
                          </div>
                        </td>
                        <td>{formatValue(beneficiary.numeroBeneficiaire)}</td>
                        <td>{formatValue(beneficiary.numeroBeneficiairePrincipal)}</td>
                        <td>{formatValue(beneficiary.lienParente)}</td>
                        <td>{formatDate(beneficiary.dateNaissance)}</td>
                        <td>{formatValue(beneficiary.telephone)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-5 text-center text-muted">
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <div className="spinner-border text-primary me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Chargement en cours...
                  </div>
                ) : (
                  <div>
                    {searchPerformed 
                      ? "Aucun bénéficiaire non synchronisé trouvé pour cette police." 
                      : "Veuillez rechercher une police pour voir les bénéficiaires non synchronisés."}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
   return {
      beneficiaries: state.contrat.beneficiaries,
      errorMessage: state.contrat.errorMessage,
      successMessage: state.contrat.successMessage,
      showLoading: state.contrat.showLoading,
      syncResult: state.contrat.syncResult,
   };
};

export default connect(mapStateToProps)(BeneficiariesSyncApp);
